/* eslint-disable react/jsx-props-no-spreading */
import { Suspense, useEffect, useRef, useState } from 'react';

import Render from 'components/render';
import { noop } from 'helpers/utils';
import AngleLeft from 'styles/assets/icons/AngleLeft';
import AngleRightIcon from 'styles/assets/icons/AngleRightIcon';
import Box from 'UIKit/Box';
import IconButton from 'UIKit/IconButton';

import CombinedTopCoinsOverviewCards from '../MarketsOverviewCards/CombinedTopCoinsOverviewCards';
import { useMarketsBanner } from '../OffersAndPromotionCards/useMarketsBanner';
import { BannersCard } from './BannerCard';
import styles from './style.module.scss';
import TopSectionSkeletonLoader from './TopSectionSkeletonLoader';

const MINIMUM_BANNERS_TO_SCROLL = 2;
const BANNERS_WIDTH = 310;

const TopSectionCards = () => {
  const cardsRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const { bannerDetails, loading } = useMarketsBanner();

  const preventHorizontalScroll = e => {
    e.preventDefault();
  };

  useEffect(() => {
    if (cardsRef?.current) {
      cardsRef?.current?.addEventListener('wheel', preventHorizontalScroll, {
        passive: false,
      });

      cardsRef?.current?.addEventListener('touchmove', preventHorizontalScroll, {
        passive: false,
      });
    }

    return () => {
      if (cardsRef?.current) {
        cardsRef?.current?.removeEventListener('wheel', preventHorizontalScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        cardsRef?.current?.removeEventListener('touchmove', preventHorizontalScroll);
      }
    };
  }, []);

  const onClickPrev = () => {
    if (!scrollPosition) return;
    setScrollPosition(scrollPosition - 1);
  };

  const onClickNext = () => {
    if (scrollPosition >= bannerDetails.length) return;
    setScrollPosition(scrollPosition + 1);
  };

  useEffect(() => {
    if (cardsRef?.current) {
      const newScrollPos = scrollPosition * BANNERS_WIDTH;
      cardsRef.current?.scrollTo(newScrollPos, 0);
    }
  }, [scrollPosition]);

  const isDataLoaded = Boolean(bannerDetails?.length > 0);
  const showSliderButtons =
    isDataLoaded && bannerDetails.length > MINIMUM_BANNERS_TO_SCROLL;

  return (
    <Box className={styles.container}>
      <Box className={styles.sliderSection}>
        <Box className={styles.prevButton} renderWhen={showSliderButtons}>
          <IconButton
            onClick={onClickPrev}
            data-testid="desktop-cards-left-icon"
            onKeyDown={noop}
            icon={<AngleLeft className={styles.arrow} />}
            disabled={!scrollPosition}
          />
        </Box>
        <Box className={styles.nextButton} renderWhen={showSliderButtons}>
          <IconButton
            onClick={onClickNext}
            data-testid="desktop-cards-right-icon"
            onKeyDown={noop}
            icon={<AngleRightIcon className={styles.arrow} />}
            disabled={scrollPosition === bannerDetails.length - MINIMUM_BANNERS_TO_SCROLL}
          />
        </Box>
        <Box className={styles.cardsWrapper} ref={cardsRef}>
          <Suspense fallback={<TopSectionSkeletonLoader count={4} />}>
            <Render
              when={isDataLoaded || loading}
              else={<TopSectionSkeletonLoader count={4} />}
            >
              <Box className={styles.cards}>
                <CombinedTopCoinsOverviewCards />
                {bannerDetails.map((banner, idx) => (
                  <BannersCard banner={banner} key={banner.id} idx={idx} />
                ))}
              </Box>
            </Render>
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

TopSectionCards.displayName = 'TopSectionCards';

export default TopSectionCards;
